import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Logistics Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Logistics was one of the
 * seven still carrying it: a two-paragraph series description under an amber note
 * saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"*,
 * the same words the Manufacturing, Pharmaceuticals, Public Services, Retail,
 * Services, Technology and Tourism cuts arrived in — with a four-part full-page
 * capture of digitopia.co's Logistics Industry Focus page as the reference, plus four
 * files: the report mockup, Figure 1, and two overlapping crops of the cover. So this
 * is the twenty-ninth designed report page and the fourteenth focus cut to arrive with
 * **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from the
 * reference capture. The *shape* of the page is certain; the *wording* is a careful
 * reading of body copy that renders a few pixels tall. Every band that carries prose
 * therefore carries a `check`, exactly as `content/daimiManufacturing.ts` and
 * `content/daimiPharmaceuticals.ts` do, and `daimiLogisticsChecks()` feeds them into
 * the same manifest as the rest of the site's amber notes so a production build cannot
 * ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda önceden
 * konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That band is
 * `content/reportClosing.ts`, shared by every report page but three, and it already
 * points at the Digital & AI Maturity Report 2026 — so this page inherits the answer
 * rather than restating it, and the reference capture agrees: its Global Perspective
 * card names the 2026 report too, and names this cut "The Logistics Focus Report",
 * which is the `shortName` `focus()` already gives it.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These documents
 * are prerendered with no client bundle at all (see `src/prerender.tsx`), so an
 * embedded form script would never run, and the other reports on this site hand the
 * reader the PDF from our own bucket instead. So every Download button here is that
 * PDF, and `DAIMI_LG_DOWNLOAD.check` is what keeps the question of gating open.
 *
 * **Not to be confused with the Future 2030 Logistics outlook**, which is a separate
 * report with its own slug, its own cover and its own designed page
 * (`content/outlookLogistics.ts`, `pages/OutlookLogistics.tsx`) — which is why the
 * constants here are `DAIMI_LG_*` and that page's are `OUTLOOK_LG_*`. Two reports, two
 * pages, one sector.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound links
 * this page exists to keep working.
 */
export const DAIMI_LG_SLUG = 'digital-and-ai-maturity-report-2026-logistics-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own "to lead in
 * 2026" line instead, and it is the page's argument in one sentence.
 *
 * The 2.89 and the 2.93 are set in bold on the live page. They are plain here, because
 * this page renders its paragraphs as strings rather than as markup — and the 2.89 is
 * the one number the page can check against itself: it is also the 2025 average in
 * Figure 1.
 */
export const DAIMI_LG_INTRO = {
  body: [
    'Logistics scores 2.89 in the 2026 DAIMI benchmark, trailing the 2.93 cross-industry baseline due to legacy debt and fragmented data. While the sector has strong operational foundations, many organizations struggle to move from local optimization to real-time network orchestration.',
    'To lead in 2026, companies must integrate data across the ecosystem and industrialize AI to turn individual nodes into a consistent, network-wide intelligence advantage.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply gets
   * a sharper cover out of it.
   *
   * **With the same caveat the Manufacturing cover had, file for file.** It arrived as
   * two captures, "(1 of 2)" and "(2 of 2)", and they are not two halves — they are
   * overlapping crops of the same square artwork, one cut off at the bottom through
   * the logo and one carrying the whole design. The second is the one held here, at
   * 2000 × 1848 rather than square, so it is missing a sliver of the top margin, and
   * the index card's square crop trims about 4% off each side. `check` asks for the
   * file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-logistics-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Logistics Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the 2.89 and the 2.93, which the capture sets in bold and which are plain here. The 2.89 is Figure 1’s 2025 average too, so the page checks itself on that one; the 2.93 cross-industry baseline is printed nowhere in the supplied files and cannot be confirmed from them. Second, the live hero has no cover beside the words — the right of it is empty but for the ribbon — and the cover is there here because that is what every other designed report page on this site does, and because the 2000px file you supplied would otherwise only ever be seen as a 400px index card. Say if the live emptiness was the point. Third, the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the fuller one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by three lines that each open with a
 * bolded label — "Core Strengths:", "The Scaling Gap:", "Industrializing AI:". Split
 * into `label` and `body` here so the label can be set rather than emphasised inside a
 * sentence, which is what a `<strong>` in the middle of a paragraph gives.
 *
 * The heading is "DAIMI Findings" and nothing after it: like the Manufacturing cut and
 * unlike the Pharmaceuticals one, this page's live heading carries no subtitle, so
 * none is invented here.
 */
export const DAIMI_LG_FINDINGS = {
  heading: 'DAIMI Findings',
  lede: 'The maturity distribution in logistics shows that while agility is growing, overall progress is still hampered by fragmentation across systems and partners.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is often built through strong operational discipline and end-to-end mindsets that allow for high-impact use cases in routing and capacity planning.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Many organizations have optimized “pockets”, such as warehouse efficiency or ETA prediction, but fail to embed these capabilities into end-to-end workflows.',
    },
    {
      label: 'Industrializing AI',
      body: 'Leaders treat AI as an operational capability embedded into dispatch and execution, while others face a “fragmentation tax” where complexity rises faster than performance.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request, the
   * same shape the Manufacturing, Automotive, Banking, Services, Retail and
   * Pharmaceuticals cuts carry rather than the vitality scatter the Technology,
   * Tourism and Public Services cuts do or the portrait score range the Utilities cut
   * does. Six years, 2020 to 2025, each one a range from the laggard through the
   * average to the leader with that year's company count in the header. It is
   * 1200 × 608 on a white ground, so it gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-logistics-study-results.webp',
    alt: 'DAIMI study results for the logistics industry, 2020 to 2025: the laggard, average and leader score each year, from 2.00–2.68–3.30 in 2020 to 2.00–2.89–3.59 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Logistics Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (3 in 2020 rising to 121 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 2.89, is the number the hero opens with, which is the one cross-check the page can do on itself; note that both the average and the leader fell in 2025 (3.06 → 2.89 and 3.72 → 3.59) and the laggard fell back to its 2020 value of 2.00, so the most recent year is the sector’s weakest since 2021 on all three lines — which is a sharper reading than “agility is growing” and which the prose does not mention. What wants a reading against digitopia.co is that prose: the lede and the three findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_LG_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Logistics',
  body: [
    'To lead in 2026, logistics executives must move beyond isolated pilots to build ecosystem-wide data interoperability. Success requires a disciplined focus on modernizing legacy interfaces and upskilling planners and dispatchers to ensure that AI-driven insights translate into faster, more reliable network decisions.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn fragmented execution into a compounding network advantage.',
  ],
  /**
   * The mockup supplied with this request — the logistics cover, not another cut's.
   * Unlike the Manufacturing cut and like most of the others, the reference capture
   * does *not* show it: the right of the live analysis band is empty. It is here
   * because `pages/FocusPage.tsx` draws it on every cut of this shape, and `check`
   * keeps that a question rather than a decision made quietly.
   */
  photo: {
    src: '/photo/daimi-report-2026-logistics-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Logistics Focus',
  } satisfies Photo,
  check:
    'Two things. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And the mockup beside them is an addition: on the live page this band is words on the left and nothing on the right, and the mockup you attached is otherwise never shown at all, so it fills that column here the way it does on the other focus pages. Say if the live emptiness was the point.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every report
 * page but three — and the reference capture for this page shows exactly that pair,
 * pointing at the Digital & AI Maturity Report 2026, which is the "2024 değil 2026"
 * the request asked for. Nothing to change: the shared band already links to
 * `DAIMI_SLUG`, and its opening sentence already names this cut "The Logistics Focus
 * Report" — the capture says "The Logistics Focus Report" too, which is the
 * `shortName` `focus()` mints.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_LG_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Logistics Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiManufacturingChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut arrived
 * with both of its figures and its cover. Three of the notes are a transcription
 * nobody has read against the original, and one is a decision about gating that
 * belongs to marketing.
 */
export function daimiLogisticsChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_LG_INTRO.check },
    { section: 'findings', check: DAIMI_LG_FINDINGS.check },
    { section: 'analysis', check: DAIMI_LG_ANALYSIS.check },
    { section: 'download', check: DAIMI_LG_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_LG_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
